"use client";

import { displayLabel } from "@/lib/display";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  transferResource,
  type Tenant,
  type TransferAffected,
  type TransferResource,
} from "@/lib/api";

interface TransferDialogProps {
  resource: TransferResource;
  resourceId: string;
  resourceLabel: string;          // e.g. playbook name, shown in description
  currentTenantId: string;
  tenants: Tenant[];              // prefetched by parent; self filtered out
  onTransferred: () => void;
  trigger?: React.ReactNode;      // custom trigger; falls back to outlined button
}

type Phase = "select" | "preview" | "executing";

export function TransferDialog({
  resource,
  resourceId,
  resourceLabel,
  currentTenantId,
  tenants,
  onTransferred,
  trigger,
}: TransferDialogProps) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>("select");
  const [target, setTarget] = useState("");
  const [affected, setAffected] = useState<TransferAffected | null>(null);

  const candidates = tenants.filter((t) => t.id !== currentTenantId);

  function reset() {
    setPhase("select");
    setTarget("");
    setAffected(null);
  }

  async function handlePreview() {
    if (!target) {
      toast.error("请选择目标租户");
      return;
    }
    const r = await transferResource(resource, resourceId, target, false);
    if ("requires_confirm" in r) {
      setAffected(r.affected);
      setPhase("preview");
    } else {
      toast.success("转移成功");
      setOpen(false);
      reset();
      onTransferred();
    }
  }

  async function handleConfirm() {
    setPhase("executing");
    const r = await transferResource(resource, resourceId, target, true);
    if ("ok" in r) {
      toast.success("转移成功");
      setOpen(false);
      reset();
      onTransferred();
    } else {
      toast.error("响应异常");
      setPhase("preview");
    }
  }

  const targetName = tenants.find((t) => t.id === target)?.name ?? target;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm">
            <ArrowRightLeft className="mr-1 h-3 w-3" /> 转移
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>转移 {displayLabel(resource)}</DialogTitle>
          <DialogDescription>
            将 <span className="font-mono">{resourceLabel}</span> 转移到另一个租户。原租户将立即失去访问权。
          </DialogDescription>
        </DialogHeader>

        {phase === "select" && (
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs text-muted-foreground">目标租户</label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="请选择目标租户..." />
                </SelectTrigger>
                <SelectContent>
                  {candidates.length === 0 ? (
                    <div className="px-2 py-4 text-center text-xs text-muted-foreground">
                      没有其他可用租户
                    </div>
                  ) : (
                    candidates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}{" "}
                        <span className="text-muted-foreground text-xs">({t.id})</span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {phase === "preview" && affected && (
          <div className="space-y-3 py-2">
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950">
              <div className="mb-2 font-medium">将转移到 {targetName}</div>
              {Object.keys(affected).length === 0 ? (
                <div className="text-muted-foreground">无衍生历史数据</div>
              ) : (
                <ul className="space-y-1 text-muted-foreground">
                  {Object.entries(affected).map(([k, v]) => (
                    <li key={k}>
                      <span>{displayLabel(k)}</span>: {v}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-2 text-xs">原租户将立即失去对该资源及以上历史数据的访问权。</div>
            </div>
          </div>
        )}

        <DialogFooter>
          {phase === "select" && (
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>
                取消
              </Button>
              <Button onClick={handlePreview} disabled={!target || candidates.length === 0}>
                预览
              </Button>
            </>
          )}
          {phase === "preview" && (
            <>
              <Button variant="outline" onClick={() => setPhase("select")}>
                返回
              </Button>
              <Button variant="destructive" onClick={handleConfirm}>
                确认转移
              </Button>
            </>
          )}
          {phase === "executing" && (
            <Button disabled>转移中...</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
